import { useTinyHost } from "@tiny/host-contract";
import React from "react";
import { Box, makeStyles, Tab, Tabs } from "@tiny/components";
import AddAudienceButton from "../../components/AddAudienceButton/AddAudienceButton";
import TinyImportDialog from "../../components/TinyImportDialog/TinyImportDialog";
import PageHeader from "@tiny/components/src/PageHeader";
import CustomSegments from "../TabCustomSegments/CustomSegments";
import AudienceSources from "../TabSources/AudienceSources";
import NoAudiencePanel from "../NoAudiencePanel";
import { useAudienceHome } from "./AudienceHome.hooks";
import PredefinedSegments from "../TabPredefinedSegments/PredefinedSegments";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { TinyImporter } from "../../components/TinyImportDialog";
import useTinyImporter from "../../hooks/useTinyImporter";
import SuppressionList from "../TabSuppressionList/SuppressionList";
import AITrafficShapingSources from "../TabAIShapping/AITrafficShapingSources";
import GlobalActiveSegmentTab from "../TabGlobalActiveSegment";
import AutoAwesome from "@tiny/icons/generated/AutoAwesome";

const useStyles = makeStyles((theme) => ({
  aiIcon: {
    color: theme.palette.primary.main,
    marginRight: 4,
    fontSize: 16,
  },
}));

function AudienceHome() {
  const { activeTab, gotoAudienceDetails, isEmpty, onChangeTab } =
    useAudienceHome();
  const {
    tinyImporterOpen,
    closeTinyImporter,
    openTinyImporter,
    openTinyImportDialog,
    closeTinyImportDialog,
    tinyImportDialogOpen,
  } = useTinyImporter();

  const classes = useStyles();

  const { smUp } = useBreakpoints();
  const [value, setValue] = React.useState<string | undefined>();

  const { capabilities } = useTinyHost();
  const isAiTrafficControlVisible = capabilities.aiTrafficControlEnabled;
  const isGlobalActiveEngagerVisible = capabilities.globalActiveEngagerVisible;
  // Default (undefined) = visible; a host can set false to hide (TinyAlbert).
  const isSuppressionListVisible =
    capabilities.suppressionListVisible !== false;

  const tabContent = ((activeTab) => {
    switch (activeTab) {
      case "sources":
        return <AudienceSources />;
      case "custom_segments":
        return <CustomSegments />;
      case "predefined_segments":
        return <PredefinedSegments />;
      case "suppression_list":
        return <SuppressionList />;
      case "ai_traffic_control":
        return <AITrafficShapingSources />;
      case "global_active_segment":
        return <GlobalActiveSegmentTab />;
      default:
        return null;
    }
  })(activeTab);

  return (
    <Box overflow={{ xs: "hidden", lg: "initial" }}>
      <PageHeader
        mb={{ md: 6 }}
        title="Audience"
        actions={<AddAudienceButton isAudienceEmpty={isEmpty} />}
      />
      {isEmpty ? (
        <NoAudiencePanel onClickUploadFile={openTinyImportDialog} />
      ) : (
        <>
          <Box mb={{ xs: 5, md: 6 }} px={{ xs: 4, md: 0 }}>
            <Tabs
              onChange={onChangeTab}
              value={activeTab}
              info="Build segments of your audiences using advanced filters."
            >
              <Tab value="sources" label="Sources" />
              {isSuppressionListVisible && (
                <Tab
                  value="suppression_list"
                  label={`${smUp ? "Suppression" : "Sup."} list`}
                />
              )}
              <Tab
                value="predefined_segments"
                label={`Predefined ${smUp ? "segments" : "seg."}`}
              />
              <Tab
                value="custom_segments"
                label={`Custom ${smUp ? "segments" : "seg."}`}
              />
              {isAiTrafficControlVisible && (
                <Tab
                  value="ai_traffic_control"
                  label={
                    smUp ? (
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <AutoAwesome className={classes.aiIcon} />
                        AI Traffic Control
                      </Box>
                    ) : (
                      "AI Tra."
                    )
                  }
                />
              )}
              {isGlobalActiveEngagerVisible && (
                <Tab
                  value="global_active_segment"
                  label={smUp ? "Global Active Segment" : "Global Seg."}
                />
              )}
            </Tabs>
          </Box>
          {tabContent}
        </>
      )}
      <TinyImportDialog
        open={tinyImportDialogOpen}
        onClose={closeTinyImportDialog}
        onSuccess={(name) => {
          setValue(name);
          openTinyImporter();
        }}
      />
      <TinyImporter
        open={tinyImporterOpen}
        onClose={closeTinyImporter}
        value={value}
        onSuccess={gotoAudienceDetails}
      />
    </Box>
  );
}

export default AudienceHome;
